import { Box, Chip } from "@material-ui/core";
import { Add as AddIcon } from "@material-ui/icons";
import { ContainerPage, Button, TitlePage } from "components";
import { OptionsMenu } from "components/OptionsMenu";
import SelectFilterMenu from "components/SelectFilterMenu";
import { useAuth } from "hooks/useAuth";
import { usePopup } from "hooks/usePopup";
import { useTableParams } from "hooks/useTableParams";
import { useState } from "react";
import { MdModeEditOutline, MdDelete } from "react-icons/md";
import { Redirect } from "react-router-dom/cjs/react-router-dom";
import sellerService from "services/sellerService";
import { Table } from "shared";
import AutoCompleteProductBase from "shared/AutoCompleteProductBase";
import { unauthorizedUsers } from "utils/checkUnauthorizedUsers";
import { formatDateToBR } from "utils/formatDate";

import { CreateCertificateDialog } from "./components/CreateCertificateDialog";
import { DeleteConfirmDialog } from "./components/DeleteConfirmDialog";
import { FilterContainer } from "./style";

export default function Certificates() {
  const { user } = useAuth();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [certificateToEdit, setCertificateToEdit] = useState(null);
  const [certificateToDelete, setCertificateToDelete] = useState(null);
  const [productBaseFilter, setProductBaseFilter] = useState(null);

  const isUnauthorizedUser = unauthorizedUsers.batch(user);

  const { data, onChange, params, status, count, handleSearch, refresh } =
    useTableParams({
      service: params => sellerService.listCertificates(params),
      paramsDefault: {
        status: undefined,
        productBase: undefined
      }
    });

  const handleOpenCreateDialog = () => {
    setCertificateToEdit(null);
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setCertificateToEdit(null);
  };

  const handleCreateSuccess = () => {
    refresh();
    handleCloseCreateDialog();
  };

  const handleEditCertificate = certificate => {
    setCertificateToEdit(certificate);
    setIsCreateDialogOpen(true);
  };

  const handleOpenDeleteDialog = certificate => {
    setCertificateToDelete(certificate);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setCertificateToDelete(null);
  };

  const handleDeleteSuccess = () => {
    refresh();
    handleCloseDeleteDialog();
  };

  const handleProductBaseFilterChange = productBase => {
    setProductBaseFilter(productBase);
    onChange({
      productBase: productBase?.id || undefined,
      page: 1
    });
  };

  const getStatusConfig = status => {
    const statusMap = {
      expired: {
        label: "Expirado",
        color: "#fff",
        backgroundColor: "#f44336"
      },
      expiring_soon: {
        label: "Expirando",
        color: "#fff",
        backgroundColor: "#ff9800"
      },
      valid: {
        label: "Válido",
        color: "#fff",
        backgroundColor: "#4caf50"
      }
    };

    return statusMap[status] || statusMap.valid;
  };

  const selectFilterProps = {
    value: params.status || "",
    text: "Status",
    onChange: status => onChange({ status: status || undefined, page: 1 }),
    options: [
      {
        label: "Expirado",
        id: "expired",
        color: { label: "#f44336", background: "#FFEBEE" }
      },
      {
        label: "Expirando",
        id: "expiring_soon",
        color: { label: "#ff9800", background: "#FFF3E0" }
      },
      {
        label: "Válido",
        id: "valid",
        color: { label: "#0a892e", background: "#e5f6ea" }
      }
    ]
  };

  const options = [
    {
      title: "Editar",
      icon: MdModeEditOutline,
      permissions: ["Tech", "Estoque"],
      action: data => handleEditCertificate(data)
    },
    {
      title: "Deletar",
      icon: MdDelete,
      color: "red",
      permissions: ["Tech", "Estoque"],
      action: data => handleOpenDeleteDialog(data)
    }
  ];

  const columns = [
    {
      field: "seller_name",
      label: "Fornecedor",
      disabledSort: true,
      style: { minWidth: "200px" },
      renderCell: ({ data }) => (
        <Box>
          <div style={{ fontWeight: 500 }}>{data.seller_name}</div>
          {data.seller_code && (
            <div style={{ fontSize: "0.75rem", color: "#757575" }}>
              Código: {data.seller_code}
            </div>
          )}
        </Box>
      )
    },
    {
      field: "product_base_name",
      label: "Produto Base",
      disabledSort: true,
      style: { minWidth: "180px" }
    },
    {
      field: "certifier",
      label: "Certificadora",
      disabledSort: true,
      style: { minWidth: "150px" }
    },
    {
      field: "name",
      label: "Nome do Certificado",
      disabledSort: true,
      style: { minWidth: "180px" }
    },
    {
      field: "issue_date",
      label: "Data de Emissão",
      align: "center",
      style: { minWidth: "130px" },
      disabledSort: true,
      formatter: value => formatDateToBR(value)
    },
    {
      field: "expiration_date",
      label: "Data de Validade",
      align: "center",
      style: { minWidth: "140px" },
      disabledSort: true,
      formatter: value => formatDateToBR(value)
    },
    {
      field: "status",
      label: "Status",
      align: "center",
      disabledSort: true,
      style: { minWidth: "120px" },
      renderCell: ({ data }) => {
        const statusConfig = getStatusConfig(data.status);
        return (
          <Chip
            label={statusConfig.label}
            size="small"
            style={{
              backgroundColor: statusConfig.backgroundColor,
              color: statusConfig.color,
              fontWeight: 500
            }}
          />
        );
      }
    },
    {
      field: "options",
      label: "",
      align: "center",
      disabledSort: true,
      renderCell: ({ data }) => <OptionsMenu data={data} options={options} />
    }
  ];

  if (isUnauthorizedUser) {
    return <Redirect to="/" />;
  }

  return (
    <ContainerPage>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <TitlePage>Certificados</TitlePage>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Novo Certificado
        </Button>
      </Box>

      <Table
        columns={columns}
        data={data}
        onChange={onChange}
        params={params}
        status={status}
        count={count}
        handleSearch={handleSearch}
        placeholder="Fornecedor, certificadora, nome..."
        filters={
          <FilterContainer>
            <AutoCompleteProductBase
              value={productBaseFilter}
              onChange={(_, value) => handleProductBaseFilterChange(value)}
              className="product-base-filter"
            />
            <SelectFilterMenu
              selectFilterProps={selectFilterProps}
              onChange={selectFilterProps.onChange}
            />
          </FilterContainer>
        }
      />

      <CreateCertificateDialog
        open={isCreateDialogOpen}
        onClose={handleCloseCreateDialog}
        onSuccess={handleCreateSuccess}
        initialCertificate={certificateToEdit}
      />

      <DeleteConfirmDialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onSuccess={handleDeleteSuccess}
        certificate={certificateToDelete}
      />
    </ContainerPage>
  );
}
